import { inject, Injectable } from '@angular/core';
import { UtilsHttpService } from 'guideline/domain';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SearchResultDto } from 'shared/feature-medical-component-search';
import { generateMD5 } from 'shared/util-common';

import {
  GuidelineLinkDto,
  GuidelineActionDto,
  ComponentLinkType,
  ObservationDefinitionDto,
  ActivityDefinitionDto,
  ComponentType,
  ConditionDefinitionDto,
  FindingDefinitionDto,
  GuidelineChapterType,
  GuidelineComponentType,
  GuidelineCriterionDto,
  ProposedComponentDto,
  GuidelineConditionComponent,
  GuidelineObservationComponent,
  GuidelineCriterionComponent,
  GuidelineActionComponent,
  GuidelineClassificationItem,
} from '@treetop/shared/model';

import { MedicationRequestService } from './medication-request.service';

@Injectable({
  providedIn: 'root',
})
export class ComponentConverter {
  private utilsHttpService = inject(UtilsHttpService);
  private medicationRequestsHttpService = inject(MedicationRequestService);

  public convertActivityToAction(
    definition: ActivityDefinitionDto,
    medicationRequests: ActivityDefinitionDto[],
    uniqueId: string,
  ): Observable<GuidelineActionDto> {
    const action: GuidelineActionDto = {
      componentType: GuidelineComponentType.Action,
    };
    const id = crypto.randomUUID();
    this.setBasicActionAttributes(definition, action, id);
    this.setActionMedicationRequests(action, medicationRequests);

    if (!definition.codings) {
      return of(action);
    }

    return this.utilsHttpService.getComponentLinks(definition.code, definition.version, uniqueId).pipe(
      map((links) => {
        return {
          ...action,
          links: [...links.map((link) => ({ ...link, fromElementUniqueId: action.uniqueId }))],
        };
      }),
    );
  }

  public convertActivityToGuidelineAction(
    definition: ActivityDefinitionDto,
  ): Observable<{ component: GuidelineActionComponent; links?: GuidelineLinkDto[] }> {
    const id = crypto.randomUUID();

    const action: Partial<GuidelineActionComponent> = {
      componentType: GuidelineComponentType.Action,
    };

    this.setBasicActionAttributes(definition, action, id);

    return of(definition).pipe(
      switchMap((definition) => {
        let getLinksPromise$: Observable<GuidelineLinkDto[]>;
        if (definition.codings) {
          getLinksPromise$ = this.utilsHttpService.getComponentLinks(definition.code, definition.version, id);
        } else {
          getLinksPromise$ = of<GuidelineLinkDto[]>([]);
        }

        if (!action.proposedComponent) {
          const medicationGroupLinks = definition.componentLinks?.filter((l) => l.linkType === ComponentLinkType.MedicationGroup);

          if (medicationGroupLinks.length > 0) {
            return this.medicationRequestsHttpService.getMedicationRequests(definition.code, definition.version).pipe(
              switchMap((medicationRequests) => {
                if (medicationRequests.length > 0) {
                  this.setActionMedicationRequests(action, medicationRequests);
                }
                return getLinksPromise$;
              }),
            );
          }
        }

        return getLinksPromise$;
      }),
      map((links): { component: GuidelineActionComponent; links?: GuidelineLinkDto[] } => {
        return {
          component: {
            ...action,
            componentType: GuidelineComponentType.Action,
          } as GuidelineActionComponent,
          links: links.length > 0 ? links : undefined,
        };
      }),
    );
  }

  public convertConditionToGuidelineCondition(definition: ConditionDefinitionDto): GuidelineConditionComponent {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      componentType: GuidelineComponentType.Condition,
      conditionDefinition: definition,
      conditionDefinitionCode: definition.code,
      conditionDefinitionVersion: definition.version,
      title: definition.title,
      description: definition.description,
    };
  }

  public convertObservationToGuidelineObservation(definition: ObservationDefinitionDto): GuidelineObservationComponent {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      componentType: GuidelineComponentType.Observation,
      observationDefinition: definition,
      observationDefinitionCode: definition.code,
      observationDefinitionVersion: definition.version,
      title: definition.title,
      description: definition.description,
      codeableConceptValues: definition.selectedCodeableConceptValues,
    };
  }

  public convertFindingToCriterion(definition: FindingDefinitionDto): GuidelineCriterionComponent {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      componentType: GuidelineComponentType.Criteria,
      findingDefinition: definition,
      findingDefinitionCode: definition.code,
      findingDefinitionVersion: definition.version,
      title: definition.title,
      description: definition.description,
    };
  }

  public convertFindingToGuidelineCriterion(definition: FindingDefinitionDto): GuidelineCriterionComponent {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      componentType: GuidelineComponentType.Criteria,
      findingDefinition: definition,
      findingDefinitionCode: definition.code,
      findingDefinitionVersion: definition.version,
      title: definition.title,
      description: definition.description,
    };
  }

  public convertProposedComponentToGuidelineComponent(
    definition: ProposedComponentDto,
    componentType: GuidelineComponentType,
  ):
    | GuidelineActionComponent
    | GuidelineConditionComponent
    | GuidelineCriterionComponent
    | GuidelineObservationComponent
    | GuidelineClassificationItem {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      proposedComponent: definition,
      proposedComponentCode: definition.code,
      componentType,
      title: definition.title,
      description: definition.description ?? null,
    };
  }

  public convertProposedComponentToAction(definition: ProposedComponentDto): GuidelineActionDto {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      componentType: GuidelineComponentType.Action,
      proposedComponent: definition,
      proposedComponentCode: definition.code,
      title: definition.title,
      description: definition.description,
    };
  }

  public convertProposedComponentToCriterion(definition: ProposedComponentDto): GuidelineCriterionDto {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      componentType: GuidelineComponentType.Criteria,
      proposedComponent: definition,
      proposedComponentCode: definition.code,
      title: definition.title,
      description: definition.description,
    };
  }

  public getGuidelineComponent(
    searchResult: SearchResultDto,
    chapterType: GuidelineChapterType,
  ): Observable<{
    component:
      | GuidelineCriterionComponent
      | GuidelineObservationComponent
      | GuidelineActionComponent
      | GuidelineConditionComponent
      | GuidelineClassificationItem;
    links?: GuidelineLinkDto[];
  }> {
    if (searchResult.componentDto) {
      switch (searchResult.componentDto.type) {
        case ComponentType.ActivityDefinition: {
          return this.convertActivityToGuidelineAction(searchResult.componentDto);
        }
        case ComponentType.ConditionDefinition:
          return of({ component: this.convertConditionToGuidelineCondition(searchResult.componentDto) });
        case ComponentType.FindingDefinition:
          return of({ component: this.convertFindingToGuidelineCriterion(searchResult.componentDto) });
        case ComponentType.ObservationDefinition:
          return of({ component: this.convertObservationToGuidelineObservation(searchResult.componentDto) });
      }
    }

    if (searchResult.proposedComponentDto) {
      const componentType = this.getComponentType(chapterType);
      return of({ component: this.convertProposedComponentToGuidelineComponent(searchResult.proposedComponentDto, componentType) });
    }

    if (searchResult.newComponentText) {
      const proposedComponent = new ProposedComponentDto();
      proposedComponent.title = searchResult.newComponentText;
      proposedComponent.code = 'PC-' + generateMD5(searchResult.newComponentText);
      const componentType = this.getComponentType(chapterType);
      return of({ component: this.convertProposedComponentToGuidelineComponent(proposedComponent, componentType) });
    }
  }

  private setActionMedicationRequests(action: GuidelineActionDto, medicationRequests: ActivityDefinitionDto[]): void {
    if (medicationRequests?.length > 0) {
      action.medicationRequests = [];
      medicationRequests.forEach((medicationRequest) => {
        const medicationRequestAction = this.convertBasicActivityToAction(medicationRequest);
        action.medicationRequests.push(medicationRequestAction);
      });
    }
  }

  private convertBasicActivityToAction(definition: ActivityDefinitionDto) {
    const action: GuidelineActionDto = {
      componentType: GuidelineComponentType.Action,
    };
    const id = crypto.randomUUID();
    this.setBasicActionAttributes(definition, action, id);

    return action;
  }

  private setBasicActionAttributes(definition: ActivityDefinitionDto, action: GuidelineActionDto, id: string) {
    action.activityDefinition = definition;
    action.activityDefinitionCode = definition.code;
    action.activityDefinitionVersion = definition.version;
    action.componentType = GuidelineComponentType.Action;
    action.title = definition.title;
    action.description = definition.description;
    action.id = id;
    action.uniqueId = id;
  }

  private getComponentType(chapterType: GuidelineChapterType): GuidelineComponentType {
    switch (chapterType) {
      case GuidelineChapterType.DiagnosisPlan:
        return GuidelineComponentType.Action;
      case GuidelineChapterType.Conditions:
        return GuidelineComponentType.Condition;
      default:
        return GuidelineComponentType.Criteria;
    }
  }
}
